import React, { useState, useEffect,useContext } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  CssBaseline,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormLabel,
  FormControl,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Collapse,
  Chip,
  useMediaQuery
} from '@mui/material';
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import { Delete as DeleteIcon } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { db } from './firebase';
import { collection, getDocs, addDoc, updateDoc, doc,query,where,deleteDoc,writeBatch } from 'firebase/firestore';
import AppContext from './AppContext';


const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [newVariant, setNewVariant] = useState({ name: '', price: 0 });
  const { shopId, t } = useContext(AppContext);
  const [isOffline, setIsOffline] = useState(false);
  const [expandedProductId, setExpandedProductId] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const showTableView = !isMobile && !isTablet;

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    // Add event listeners for online and offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial status
    setIsOffline(!navigator.onLine);

    // Clean up event listeners on component unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const fetchCategoriesAndProducts = async () => {
      try {
        const categoryQuery = query(collection(db, 'categories'), where('shopId', '==', shopId));
        const productQuery = query(collection(db, 'products'), where('shopId', '==', shopId));
  
        const categorySnapshot = await getDocs(categoryQuery);
        const productSnapshot = await getDocs(productQuery);
        
        const fetchedCategories = categorySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const fetchedProducts = productSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
        // Sort products by name (A-Z)
        const sortedProducts = fetchedProducts.sort((a, b) => a.name.localeCompare(b.name));
  
        setCategories(fetchedCategories);
        setProducts(sortedProducts);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data from Firebase:", error);
        setError(t("Failed to load data."));
        setLoading(false);
      }
    };
  
    fetchCategoriesAndProducts();
  }, [shopId]);
  

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditClick = (product) => {
    setEditProduct({ ...product });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditProduct(null);
    setNewVariant({ name: '', price: 0 });
  };

  const handleSaveProduct = async () => {
    try {
      if (editProduct.id) {
        // Update existing product in Firestore
        const productRef = doc(db, 'products', editProduct.id);
        await updateDoc(productRef, {
          ...editProduct,
          shopId, // Ensure shopId is included when updating
        });
  
        // Update the local state
        setProducts(products.map((p) => (p.id === editProduct.id ? editProduct : p)));
      } else {
        // Add new product to Firestore
        const docRef = await addDoc(collection(db, 'products'), {
          ...editProduct,
          shopId, // Ensure shopId is included when adding a new product
        });
  
        // Update the local state with the new product, using the newly created doc ID
        const newProduct = { id: docRef.id, ...editProduct };
        setProducts([...products, newProduct]);
      }
    } catch (error) {
      console.error("Error saving product to Firebase:", error);
    }
    handleCloseDialog();
  };

  const handleDeleteProduct = async (productId) => {
    try {
      // Delete the product from Firestore
      const productRef = doc(db, 'products', productId);
      await deleteDoc(productRef);
  
      // Remove the product from the local state
      setProducts(products.filter((product) => product.id !== productId));
    } catch (error) {
      console.error("Error deleting product from Firebase:", error);
    }
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setEditProduct({ 
      ...editProduct, 
      [name]: type === 'checkbox' ? checked : (name === 'price' ? Number(value) : value)
    });
  };

  const handleVariantChange = (index, field, value) => {
    const updatedVariants = [...editProduct.variants];
    updatedVariants[index][field] = field === 'price' ? Number(value) : value;
    setEditProduct({ ...editProduct, variants: updatedVariants });
  };

  const handleAddVariant = () => {
    if (newVariant.name && newVariant.price !== '') {
      setEditProduct({
        ...editProduct,
        variants: [...editProduct.variants, newVariant],
      });
      setNewVariant({ name: '', price: 0 });
    }
  };

  const handleRemoveVariant = (index) => {
    const updatedVariants = editProduct.variants.filter((_, i) => i !== index);
    setEditProduct({ ...editProduct, variants: updatedVariants });
  };

  const handleAddNewClick = () => {
    setEditProduct({
      name: '',
      category: '',
      price: 0,
      variants: [],
      inscription: false,
      productType: '', // Add new product type field
    });
    setOpenDialog(true);
  };

  const handleCopyProducts=async()=>{
    let oldShopId="Eureka";
    let newShopId=shopId;
    try {
      // Step 1: Fetch documents with oldShopId
      const productsQuery = query(
        collection(db, 'products'),  // Reference the 'products' collection
        where('shopId', '==', oldShopId)
      );
      const productsSnapshot = await getDocs(productsQuery);
  
      // Step 2: Create a batch for copying documents
      const batch = writeBatch(db);  // Create a batch to perform multiple writes
  
      // Step 3: Copy each document with the new shopId
      productsSnapshot.forEach((docSnapshot) => {
        const newDocRef = doc(collection(db, 'products'));  // Create a new document reference
        const productData = docSnapshot.data();
  
        // Modify the shopId to the new one
        productData.shopId = newShopId;
  
        // Add the operation to the batch
        batch.set(newDocRef, productData);
      });
  
      // Step 4: Commit the batch
      await batch.commit();
      console.log('Products copied successfully to new shopId!');

      const categoryQuery = query(
        collection(db, 'categories'),  // Reference the 'products' collection
        where('shopId', '==', oldShopId)
      );
      const categorySnapshot = await getDocs(categoryQuery);
  
      // Step 2: Create a batch for copying documents
      const batch2 = writeBatch(db);  // Create a batch to perform multiple writes
  
      // Step 3: Copy each document with the new shopId
      categorySnapshot.forEach((docSnapshot) => {
        const newDocRef = doc(collection(db, 'categories'));  // Create a new document reference
        const productData = docSnapshot.data();
  
        // Modify the shopId to the new one
        productData.shopId = newShopId;
  
        // Add the operation to the batch
        batch2.set(newDocRef, productData);
      });
  
      // Step 4: Commit the batch
      await batch2.commit();
      console.log('Categories copied successfully to new shopId!');
    } catch (error) {
      console.error('Error copying products:', error);
    }
  }

  const handleCardToggle = (productId) => {
    setExpandedProductId(prev => (prev === productId ? null : productId));
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container>
      {isOffline && (
            <Alert severity="warning" sx={{ marginTop: '10px' }}>
              {t("You are offline. Some features may not be available.")}
            </Alert>
          )}
        <Typography variant="h4" gutterBottom>
          {t("Product Management")}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("Search Products")}
              variant="outlined"
              value={searchTerm}
              onChange={handleSearch}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button variant="contained" color="primary" onClick={handleAddNewClick} disableElevation fullWidth>
              {t("Add Product")}
            </Button>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button variant="contained" color="primary" onClick={handleCopyProducts} disableElevation fullWidth>
              {t("Copy from Master")}
            </Button>
          </Grid>
        </Grid>
        {showTableView ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("Item Name")}</TableCell>
                  <TableCell>{t("Category")}</TableCell>
                  <TableCell>{t("Item Type")}</TableCell>
                  <TableCell>{t("Variations")}</TableCell>
                  <TableCell>{t("Base Price")}</TableCell>
                  <TableCell>{t("Inscription")}</TableCell>
                  <TableCell>{t("Actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>{product.category}</TableCell>
                    <TableCell>{product.productType}</TableCell>
                    <TableCell>
                      {product.variants.length === 0 ? t("...") : t("None")}
                    </TableCell>
                    <TableCell>{product.price}</TableCell>
                    <TableCell>{product.inscription ? t("...") : t("N/A")}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditClick(product)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteProduct(product.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {filteredProducts.map((product) => {
              const productVariants = Array.isArray(product.variants) ? product.variants : [];
              const isExpanded = expandedProductId === product.id;
              return (
                <Grid item xs={12} key={product.id}>
                  <Card
                    variant="outlined"
                    onClick={() => handleCardToggle(product.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {t("Product")}
                          </Typography>
                          <Typography variant="h6">{product.name || t("Unnamed Product")}</Typography>
                        </Box>
                        <Chip
                          label={product.productType ? t(product.productType) : t("Unknown")}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        {t("Category")}: {product.category || t("Uncategorized")}
                      </Typography>
                      <Typography variant="body1" mt={1}>
                        {t("Base Price")}: {product.price ?? 0}
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        {t("Inscription")}: {product.inscription ? t("Yes") : t("No")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        {t("Variants")}: {productVariants.length}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                      <Button
                        size="small"
                        color="primary"
                        startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      >
                        {isExpanded ? t("Hide Details") : t("View Details")}
                      </Button>
                      <Box>
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEditClick(product);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDeleteProduct(product.id);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardActions>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Divider />
                      <Box sx={{ p: 2 }}>
                        {productVariants.length > 0 ? (
                          productVariants.map((variant, index) => (
                            <Box key={index} sx={{ mb: index === productVariants.length - 1 ? 0 : 1.5 }}>
                              <Typography variant="subtitle2">{variant.name || `${t("Variant")} ${index + 1}`}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {t("Price")}: {variant.price ?? 0}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t("No variants configured.")}
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{editProduct && editProduct.id ? t("Edit Product") : t("Add Product")}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label={t("Product Name")}
              fullWidth
              value={editProduct?.name || ''}
              onChange={handleInputChange}
            />
            <FormControl fullWidth>
              <FormLabel>{t("Category")}</FormLabel>
              <Select
                margin="dense"
                name="category"
                fullWidth
                value={editProduct?.category || ''}
                onChange={handleInputChange}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.name}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <FormLabel>{t("Product Type")}</FormLabel>
              <Select
                margin="dense"
                name="productType"
                fullWidth
                value={editProduct?.productType || ''}
                onChange={handleInputChange}
              >
                <MenuItem value="Order">{t("Order")}</MenuItem>
                <MenuItem value="Sale">{t("Sale")}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              margin="normal"
              name="price"
              label={t("Base Price")}
              type="number"
              fullWidth
              value={editProduct?.price || ''}
              onChange={handleInputChange}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editProduct?.inscription || false}
                  onChange={handleInputChange}
                  name="inscription"
                  color="primary"
                />
              }
              label={t("Inscription Available")}
            />
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("Variants")}
              </Typography>
              <List>
                {editProduct?.variants.map((variant, index) => (
                  <ListItem key={index}>
                    <TextField
                      label={t("Variant Name")}
                      value={variant.name}
                      onChange={(e) =>
                        handleVariantChange(index, 'name', e.target.value)
                      }
                      sx={{ mr: 2 }}
                    />
                    <TextField
                      label={t("Variant Price")}
                      type="number"
                      value={variant.price}
                      onChange={(e) =>
                        handleVariantChange(index, 'price', e.target.value)
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" aria-label={t("Delete")} onClick={() => handleRemoveVariant(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              <TextField
                label={t("New Variant Name")}
                value={newVariant.name}
                onChange={(e) =>
                  setNewVariant({ ...newVariant, name: e.target.value })
                }
                sx={{ m: 1 }}
              />
              <TextField
                label={t("New Variant Price")}
                type="number"
                value={newVariant.price}
                onChange={(e) =>
                  setNewVariant({ ...newVariant, price: Number(e.target.value) })
                }
                sx={{m:1}}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddVariant}
                disableElevation
                sx={{ m: 1 }}
              >
                {t("Add Variant")}
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="secondary" disableElevation>
              {t("Cancel")}
            </Button>
            <Button onClick={handleSaveProduct} color="primary" disableElevation>
              {t("Save")}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
};

export default Products;
